import { useState } from "react";
import { Instagram } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export function InstagramImport({ onAdd }) {
  const [profile, setProfile] = useState("");
  const [links, setLinks] = useState("");
  const [gallery, setGallery] = useState([]);
  const [selected, setSelected] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [importing, setImporting] = useState(false);

  const fetchProfile = async () => {
    if (!profile.trim()) return toast.error("Paste your Instagram profile link or username first");
    setFetching(true);
    try {
      const r = await api.post("/admin/import-profile", { profile: profile.trim() });
      if (!r.data.images || !r.data.images.length) {
        toast.error(r.data.note || "Instagram blocked the profile fetch — paste post links below instead");
      } else {
        setGallery((g) => [...g, ...r.data.images.map((src) => ({ src, stored: null })).filter((n) => !g.some((x) => x.src === n.src))]);
        toast.success(`Found ${r.data.images.length} photos — tap to select`);
      }
    } catch (err) {
      toast.error(formatApiError(err, "Could not fetch the profile. Paste post links below instead."));
    } finally {
      setFetching(false);
    }
  };

  const importLinks = async () => {
    const urls = links.split("\n").map((u) => u.trim()).filter(Boolean);
    if (!urls.length) return toast.error("Paste one or more Instagram post links");
    setImporting(true);
    try {
      const r = await api.post("/admin/import-bulk", { urls });
      const items = r.data.imported.map((x) => ({ src: x.source, stored: x.url }));
      setGallery((g) => [...g, ...items.filter((n) => !g.some((x) => x.src === n.src))]);
      r.data.failed.forEach((f) => toast.error(f.error));
      if (items.length) toast.success(`${items.length} photo(s) imported — tap to select, then Add Selected`);
      setLinks("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImporting(false);
    }
  };

  const toggle = (i) => setSelected((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));

  const addSelected = async () => {
    if (!selected.length) return toast.error("Tap photos in the gallery to select them first");
    setImporting(true);
    try {
      const need = selected.filter((i) => !gallery[i].stored);
      const storedNow = {};
      if (need.length) {
        const r = await api.post("/admin/import-bulk", { urls: need.map((i) => gallery[i].src) });
        r.data.imported.forEach((x) => { storedNow[x.source] = x.url; });
        r.data.failed.forEach((f) => toast.error(f.error));
      }
      const finalUrls = selected.map((i) => gallery[i].stored || storedNow[gallery[i].src]).filter(Boolean);
      if (!finalUrls.length) return toast.error("Those photos could not be imported");
      onAdd(finalUrls);
      toast.success(`${finalUrls.length} photo(s) added to the product`);
      setGallery([]);
      setSelected([]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="instagram-import-panel" className="rounded-3xl border border-gold-line bg-white p-5 sm:p-6 space-y-4">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold-soft text-maroon">
          <Instagram className="w-4 h-4" />
        </span>
        <h2 className="font-display text-2xl font-semibold text-maroon-dark">Import from Instagram</h2>
      </div>

      <div className="space-y-2">
        <p className="text-xs text-ink-muted"><span className="font-semibold text-ink">1.</span> Fetch recent photos from your profile (if Instagram allows):</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            data-testid="instagram-import-profile-input"
            value={profile}
            onChange={(e) => setProfile(e.target.value)}
            placeholder="iralakshmi.fashion or profile link"
            className="border-gold-line"
          />
          <Button
            data-testid="instagram-fetch-btn"
            type="button"
            onClick={fetchProfile}
            disabled={fetching}
            className="rounded-full bg-maroon hover:bg-maroon-dark text-white font-semibold px-8 disabled:opacity-50"
          >
            {fetching ? "Fetching..." : "Fetch Photos"}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs text-ink-muted"><span className="font-semibold text-ink">2.</span> Or paste Instagram post links (one per line) — always works:</p>
        <Textarea
          data-testid="instagram-links-textarea"
          value={links}
          onChange={(e) => setLinks(e.target.value)}
          placeholder={"https://www.instagram.com/p/Cxyz...\nhttps://www.instagram.com/p/Abcd..."}
          rows={3}
          className="border-gold-line"
        />
        <Button
          data-testid="instagram-import-btn"
          type="button"
          onClick={importLinks}
          disabled={importing}
          className="rounded-full bg-maroon hover:bg-maroon-dark text-white font-semibold px-8 disabled:opacity-50"
        >
          {importing ? "Importing..." : "Import These Links"}
        </Button>
      </div>

      {gallery.length > 0 ? (
        <div className="space-y-3 pt-2 border-t border-gold-line">
          <p className="text-xs text-ink-muted"><span className="font-semibold text-ink">3.</span> Tap photos to select, then add them to the product:</p>
          <div data-testid="instagram-gallery" className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {gallery.map((item, i) => (
              <button
                key={item.src + i}
                type="button"
                data-testid={`instagram-gallery-item-${i}`}
                onClick={() => toggle(i)}
                className={`relative overflow-hidden rounded-lg border-2 transition-all duration-200 ${selected.includes(i) ? "border-maroon ring-2 ring-gold" : "border-gold-line hover:border-gold"}`}
              >
                <img src={item.stored ? fileUrl(item.stored) : item.src} alt={`Found ${i + 1}`} className="aspect-square w-full object-cover" />
                {selected.includes(i) ? <span className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-maroon text-[10px] font-bold text-white">{selected.indexOf(i) + 1}</span> : null}
              </button>
            ))}
          </div>
          <Button
            data-testid="instagram-add-selected-btn"
            type="button"
            onClick={addSelected}
            disabled={importing || !selected.length}
            className="rounded-full bg-whats hover:bg-whats-dark text-white font-semibold px-8 disabled:opacity-50"
          >
            {importing ? "Working..." : `Add ${selected.length || ""} Selected to Product`}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

